import { ajvResolver } from '@hookform/resolvers/ajv'
import ListIcon from '@mui/icons-material/List'
import { Box, Checkbox, FormControlLabel, FormGroup, Grid, MenuItem, Paper, TextField } from '@mui/material'
import { FormAlert, FormHeader } from 'components'
import { IMenuOptionDTO } from 'data/dtos/security/i-menu-option-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import MenuOptionSchema, { IMenuOptionFormData } from 'schema/security/menu-option'
import api from 'services/api'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const MenuOptionForm: React.FC = () => {
  const [mainError, setMainError] = useState('')
  const [modules, setModules] = useState([])

  const params = useParams<IRouteParams>()
  const firstInputElement = useRef<any>(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
  } = useForm<IMenuOptionFormData>({
    resolver: ajvResolver(MenuOptionSchema),
    defaultValues: {
      moduleId: '',
      sequence: '',
      label: '',
      route: '',
      icon: '',
      key: '',
      disabled: false,
    },
  })

  // initial load

  useEffect(() => {
    async function loadData() {
      // select Modulo

      await api
        .post('/modules/select')
        .then((response) => {
          const { data } = response.data

          return data
        })
        .then((modulesResult) => {
          setModules(modulesResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    loadData()
  }, [])

  // main data load

  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data

      await api
        .get(`/menu-options/${id}`)
        .then((response) => {
          const { data } = response.data

          const menuOptionResult = {
            moduleId: data.moduleId.id,
            sequence: data.sequence,
            label: data.label,
            route: data.route,
            icon: data.icon,
            key: data.key,
            disabled: data.disabled,
          }

          return menuOptionResult
        })
        .then((menuOptionResult: IMenuOptionFormData) => {
          reset(menuOptionResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
    }
  }, [params, params.id])

  // data save

  const onSubmit: SubmitHandler<IMenuOptionFormData> = useCallback(async (data) => {
    const payLoad: IMenuOptionDTO = {
      moduleId: data.moduleId,
      sequence: data.sequence,
      label: data.label,
      route: data.route,
      icon: data.icon,
      key: data.key,
      disabled: data.disabled,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/menu-options`, payLoad)
        .then(history.push('/menu-options'))
        .catch((error) => {
          console.log(error.response?.data)
          setMainError(error.response?.data.data.name)
          return error.response?.data.data
        })
    } else {
      await api
        .post('/menu-options', payLoad)
        .then(history.push('/menu-options/new'))
        .then(() => reset())
        .then(() =>
          setTimeout(() => {
            firstInputElement.current.focus()
          }, 0)
        )
        .catch((error) => {
          console.log(error.response?.data)
          setMainError(error.response?.data.data.name)
          return error.response?.data.data
        })
    }
  }, [])

  const handleChange = (formField: any) => {
    setMainError('')
  }

  return (
    <Paper
      elevation={3}
      className={classes.paper}
    >
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        data-testid='form'
      >
        <FormHeader
          title='Opções de Menu'
          icon={ListIcon}
          backRoute='/menu-options'
          showSaveButton={true}
          helpText='Nesta opção serão informados os menus de opção da aplicação.'
        />

        <FormAlert
          setMainError={setMainError}
          mainError={mainError}
        />

        <Grid
          container
          spacing={1}
          className={mainError === '' ? classes.formContainer : classes.formContainerWithError}
        >
          <Grid
            item
            xs={12}
          >
            <TextField
              id='moduleId'
              label='Modulo'
              error={!!errors.moduleId}
              helperText={errors?.moduleId?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              value={`${watch().moduleId}`}
              select
              autoFocus
              inputRef={firstInputElement}
              {...register('moduleId', {
                onChange: (e) => {
                  setValue('moduleId', e.target.value)
                  handleChange(e)
                },
              })}
            >
              {modules.map((module: any) => (
                <MenuItem
                  key={module.id}
                  value={module.id}
                >
                  {module.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid
            item
            xs={12}
          >
            <TextField
              id='sequence'
              label='Sequência'
              error={!!errors.sequence}
              helperText={errors?.sequence?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 20,
              }}
              {...register('sequence', { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <TextField
              id='label'
              label='Título'
              error={!!errors.label}
              helperText={errors?.label?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 60,
              }}
              {...register('label', { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <TextField
              id='route'
              label='Rota'
              error={!!errors.route}
              helperText={errors?.route?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 100,
              }}
              {...register('route', { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <TextField
              id='icon'
              label='Ícone'
              error={!!errors.icon}
              helperText={errors?.icon?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 20,
              }}
              {...register('icon', { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <TextField
              id='key'
              label='Identificador'
              error={!!errors.key}
              helperText={errors?.key?.message}
              variant='outlined'
              margin='dense'
              size='small'
              fullWidth={true}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                maxLength: 255,
              }}
              {...register('key', { onChange: (e) => handleChange(e) })}
            />
          </Grid>

          <Grid
            item
            xs={12}
          >
            <FormGroup className={classes.checkBox}>
              <Controller
                name='disabled'
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        onChange={(e) => field.onChange(e.target.checked)}
                        checked={field.value}
                      />
                    }
                    label='Inativo'
                  />
                )}
              />
            </FormGroup>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  )
}

export default MenuOptionForm
